'use strict';

// This is the same as the PAGE_TYPE enum in clinic-v4/app/services/IframeService.ts
const PAGE_TYPE = {
  home: 'home',
  appointmentList: 'appointmentList',
  membership: 'membership',
  billing: 'billing',
  reports: 'reports',
  myAccount: 'myAccount',
  previewProfile: 'previewProfile',
  support: 'support',
  opencareTutorial: 'opencareTutorial',
  logout: 'logout',
};

angular.module('ClinicApp').directive('sidebar', (
  $state,
  ClinicConfig,
  CurrentUser,
  IframeService,
  OCLogUtil,
  OpencareConstants,
  UserService,
  ClinicService,
) => {
  return {
    replace: true,
    templateUrl: '/views/directives/_sidebar.html',
    link: function($scope, $element) {
      const iframeContainer = $element[0];
      const iframeElement = iframeContainer.querySelector('#js-sidebar-iframe');
      const sidebarUri = encodeURI(`${ClinicConfig.CLINIC_APP_V4_URL}/#/sidebar`);
      iframeElement.src = sidebarUri;

      const updateWindowSize = () => {
        const { innerHeight, innerWidth } = window;
        if (!innerHeight || !innerWidth) {
          return;
        }

        IframeService.sendMessage({
          iframe: iframeElement,
          type: 'windowSize',
          data: {
            width: innerWidth,
            height: innerHeight,
          },
        });
      };

      window.addEventListener('resize', updateWindowSize);
      $scope.$on('$destroy', () => {
        window.removeEventListener('resize', updateWindowSize);
      });

      const getCurrentRouteParams = () => ({
        routeName: $state.current?.name,
        routeParams: $state.params,
      });

      const sendCurrentRouteParams = () => IframeService.sendMessage({
        iframe: iframeElement,
        type: 'currentRouteParams',
        data: getCurrentRouteParams(),
      });

      // We watch this instead of using $rootScope.$on('$stateChangeSuccess')
      // since $stateChangeSuccess doesn't fire on refresh
      $scope.$watch(
        getCurrentRouteParams,
        sendCurrentRouteParams,
        true, // object equality
      );

      // clinic-v4 will call this when its ready to receive messages
      // So it can have the correct clinic-v3 data on first load
      $scope.iframeReady = () => {
        updateWindowSize();
        sendCurrentRouteParams();
      };

      // We would normally use IFrameService.resizeHandler, but we need to do some custom logic here
      IframeService.onMessage('resize-sidebar', (data) => {
        const { height, width } = data;

        if (typeof height !== 'number' && typeof width !== 'number') {
          return;
        }

        // In mobile, apply padding to the page wrapper to account for the height of the sidebar
        (() => {
          const pageWrapper = document.querySelector('#page-wrapper');
          if (!pageWrapper) {
            return;
          }

          const screenWidth = window.innerWidth;
          const isMobile = screenWidth < 992;

          if (!isMobile) {
            pageWrapper.style.removeProperty('padding-top');
            return;
          }
          // 127 is the max height of the sidebar on mobile before any menus are opened when the clinic selector is visible
          pageWrapper.style.paddingTop = `${Math.min(height, 127)}px`;
        })();

        if (width) {
          iframeContainer.style.width = `${width}px`;
        }

        if (height) {
          iframeContainer.style.height = `${height}px`;
        }
      });

      const goToState = (state, params = {}) => {
        if ($state.current?.name === state && JSON.stringify($state.params) === JSON.stringify(params)) {
          return;
        }

        $state.go(state, params);
        window.scrollTo(0, 0);
      };

      const PAGE_TYPE_MAP = {
        [PAGE_TYPE.home]: () => {
          goToState('home');
        },
        [PAGE_TYPE.appointmentList]: ({ data }) => {
          if (data.clinicId === ClinicService.getAllClinicsId()) {
            goToState('all-appointments');
            return;
          }

          goToState('clinic.appointments', { id: data.clinicId });
        },
        [PAGE_TYPE.membership]: () => {
          goToState('opencare-membership');
        },
        [PAGE_TYPE.billing]: () => {
          goToState('user-prime');
        },
        [PAGE_TYPE.reports]: () => {
          goToState('reports');
        },
        [PAGE_TYPE.myAccount]: () => {
          goToState('user-account');
        },
        [PAGE_TYPE.previewProfile]: ({ data }) => {
          if (!data.clinicId) {
            OCLogUtil.rollbar.error('No clinic ID provided to preview profile', { data });
            return;
          }

          const clinicProfilePage = `${ClinicConfig.SEARCH_APP_V2_URL}/results/${data.clinicId}`;
          window.open(clinicProfilePage);
        },
        [PAGE_TYPE.support]: () => {
          window.open(OpencareConstants.supportUrl, '_blank');
        },
        [PAGE_TYPE.opencareTutorial]: () => {
          window.open(OpencareConstants.tutorialUrl, '_blank');
        },
        [PAGE_TYPE.logout]: () => {
          // Wrap this because our process of building will
          // call this with regeneratorRuntime which isn't defined
          // on the root level.
          // This component is rendered so early that the function is called
          // before regeneratorRuntime is defined
          (async () => {
            OpencareAnalytics.clearUserId();
            await UserService.logout();
            await CurrentUser.updateAsync();
            goToState('login');
          })();
        },
      };

      $scope.goToPage = ({ page, ...data }) => {
        // Wrap this because our process of building will
        // define this when regeneratorRuntime isn't defined
        (async () => {
          if (!Object.values(PAGE_TYPE).includes(page)) {
            OCLogUtil.rollbar.error('Invalid page type', { page });
            return;
          }

          await PAGE_TYPE_MAP[page]({ data });
        })();
      };

      IframeService.setupFunctionCallHandler($scope);
    },
  };
});
